import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Card,
  CardBody,
  Container,
  Row,
  Col,
  Table,
  Badge,
  Button,
  Spinner,
  Alert,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Breadcrumb from "../../components/Common/Breadcrumbs";
import { customerReturnActions } from "../../sagas/customerReturnSlice";
import { CUSTOMER_RETURNS_PAGE, CUSTOMER_RETURN_FORM_PAGE } from "../../routes/routeConstants";
import { formatDate, formatPrice } from "../../helpers/utility";

const CustomerReturnDetail = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const { currentReturn, loading } = useSelector((state) => state.customerReturns);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    dispatch(customerReturnActions.fetchById(id));
  }, [dispatch, id]);

  const getStatusBadge = (status) => {
    switch (status) {
      case "BROUILLON":
        return <Badge color="warning">{t("customerReturns.status.BROUILLON")}</Badge>;
      case "COMPTABILISE":
        return <Badge color="success">{t("customerReturns.status.COMPTABILISE")}</Badge>;
      default:
        return <Badge color="secondary">{status}</Badge>;
    }
  };

  const getRefundModeBadge = (mode) => {
    switch (mode) {
      case "REMBOURSEMENT":
        return <Badge color="info">{t("customerReturns.refundMode.REMBOURSEMENT")}</Badge>;
      case "AVOIR":
        return <Badge color="primary">{t("customerReturns.refundMode.AVOIR")}</Badge>;
      case "ECHANGE":
        return <Badge color="dark">{t("customerReturns.refundMode.ECHANGE")}</Badge>;
      default:
        return <Badge color="secondary">{mode}</Badge>;
    }
  };

  const handleValidate = () => {
    if (!window.confirm(t("customerReturns.messages.confirmValidate"))) {
      return;
    }

    setProcessing(true);
    dispatch(
      customerReturnActions.validate({
        id: id,
        onSuccess: () => {
          toast.success(t("customerReturns.messages.validateSuccess"));
          dispatch(customerReturnActions.fetchById(id));
          setProcessing(false);
        },
        onError: (error) => {
          toast.error(t("customerReturns.messages.validateError"));
          setProcessing(false);
        },
      })
    );
  };

  const handleCancel = () => {
    if (!window.confirm(t("customerReturns.messages.confirmCancel"))) {
      return;
    }

    setProcessing(true);
    dispatch(
      customerReturnActions.cancel({
        id: id,
        onSuccess: () => {
          toast.success(t("customerReturns.messages.cancelSuccess"));
          dispatch(customerReturnActions.fetchById(id));
          setProcessing(false);
        },
        onError: (error) => {
          toast.error(t("customerReturns.messages.cancelError"));
          setProcessing(false);
        },
      })
    );
  };

  const handleDelete = () => {
    if (!window.confirm(t("customerReturns.messages.confirmDelete"))) {
      return;
    }

    setProcessing(true);
    dispatch(
      customerReturnActions.delete({
        id: id,
        onSuccess: () => {
          toast.success(t("customerReturns.messages.deleteSuccess"));
          navigate(CUSTOMER_RETURNS_PAGE);
        },
        onError: (error) => {
          toast.error(t("customerReturns.messages.deleteError"));
          setProcessing(false);
        },
      })
    );
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center my-5">
            <Spinner color="primary" />
          </div>
        </Container>
      </div>
    );
  }

  if (!currentReturn) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert color="danger">
            {t("customerReturns.detail.notFound")}
          </Alert>
          <div className="text-center mt-4">
            <Link to={CUSTOMER_RETURNS_PAGE} className="btn btn-primary">
              {t("customerReturns.detail.back")}
            </Link>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb
            title={t("customerReturns.title")}
            breadcrumbItem={t("customerReturns.detail.title")}
          />

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="card-title">{t("customerReturns.detail.title")}</h4>
                    <div className="d-flex gap-2">
                      <Link to={CUSTOMER_RETURNS_PAGE} className="btn btn-secondary">
                        <i className="bx bx-arrow-back me-1"></i> {t("customerReturns.detail.back")}
                      </Link>
                      {currentReturn.statut === "BROUILLON" && (
                        <>
                          <Link
                            to={CUSTOMER_RETURN_FORM_PAGE.replace(":id?", id)}
                            className="btn btn-info"
                            disabled={processing}
                          >
                            <i className="bx bx-edit me-1"></i> {t("customerReturns.detail.edit")}
                          </Link>
                          <Button
                            color="success"
                            onClick={handleValidate}
                            disabled={processing}
                          >
                            {processing ? (
                              <Spinner size="sm" />
                            ) : (
                              <>
                                <i className="bx bx-check me-1"></i> {t("customerReturns.detail.validate")}
                              </>
                            )}
                          </Button>
                          <Button
                            color="danger"
                            onClick={handleDelete}
                            disabled={processing}
                          >
                            <i className="bx bx-trash me-1"></i> {t("customerReturns.detail.cancel")}
                          </Button>
                        </>
                      )}
                      {currentReturn.statut === "COMPTABILISE" &&
                        currentReturn.modeRemboursement === "REMBOURSEMENT" &&
                        currentReturn.estRemboursable && (
                          <Button
                            color="primary"
                            onClick={() => navigate(`/ventes/remboursements/new?retourId=${id}`)}
                          >
                            <i className="bx bx-money me-1"></i> {t("customerReturns.detail.refund")}
                          </Button>
                        )}
                    </div>
                  </div>

                  <Row className="mb-4">
                    <Col md={6}>
                      <div className="mb-3">
                        <h5 className="font-size-15">{t("customerReturns.detail.generalInfo")}</h5>
                        <div className="table-responsive">
                          <Table className="table-borderless mb-0">
                            <tbody>
                              <tr>
                                <th scope="row" style={{ width: "40%" }}>
                                  {t("customerReturns.detail.fields.returnNumber")}
                                </th>
                                <td>{currentReturn.numeroRetour}</td>
                              </tr>
                              <tr>
                                <th scope="row">
                                  {t("customerReturns.detail.fields.returnDate")}
                                </th>
                                <td>{formatDate(currentReturn.dateOperation)}</td>
                              </tr>
                              <tr>
                                <th scope="row">{t("customerReturns.detail.fields.status")}</th>
                                <td>{getStatusBadge(currentReturn.statut)}</td>
                              </tr>
                              <tr>
                                <th scope="row">
                                  {t("customerReturns.detail.fields.refundMode")}
                                </th>
                                <td>
                                  {getRefundModeBadge(currentReturn.modeRemboursement)}
                                </td>
                              </tr>
                            </tbody>
                          </Table>
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <h5 className="font-size-15">{t("customerReturns.detail.clientAndInvoice")}</h5>
                        <div className="table-responsive">
                          <Table className="table-borderless mb-0">
                            <tbody>
                              <tr>
                                <th scope="row" style={{ width: "40%" }}>
                                  {t("customerReturns.detail.fields.client")}
                                </th>
                                <td>
                                  <Link to={`/clients/${currentReturn.client?.id}`}>
                                    {currentReturn.client?.nom}
                                  </Link>
                                </td>
                              </tr>
                              <tr>
                                <th scope="row">
                                  {t("customerReturns.detail.fields.originalInvoice")}
                                </th>
                                <td>
                                  <Link
                                    to={`/ventes/factures/${currentReturn.factureOrigine?.id}`}
                                  >
                                    {currentReturn.factureOrigine?.numeroFacture}
                                  </Link>
                                </td>
                              </tr>
                              <tr>
                                <th scope="row">{t("customerReturns.detail.fields.reason")}</th>
                                <td>{currentReturn.motifRetour}</td>
                              </tr>
                            </tbody>
                          </Table>
                        </div>
                      </div>
                    </Col>
                  </Row>

                  <div className="mb-4">
                    <h5 className="font-size-15">{t("customerReturns.detail.returnedItems")}</h5>
                    <div className="table-responsive">
                      <Table className="table-centered table-nowrap mb-0">
                        <thead>
                          <tr>
                            <th>{t("customerReturns.form.columns.item")}</th>
                            <th>{t("customerReturns.form.columns.quantity")}</th>
                            <th>{t("customerReturns.form.columns.unitPrice")}</th>
                            <th>{t("customerReturns.form.columns.vat")}</th>
                            <th>{t("customerReturns.form.columns.total")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentReturn.lignes.map((line, index) => {
                            const lineTotal = line.quantite * line.prixUnitaireHT;
                            return (
                              <tr key={index}>
                                <td>{line.article?.designation}</td>
                                <td>{line.quantite}</td>
                                <td>{formatPrice(line.prixUnitaireHT)}</td>
                                <td>{line.tauxTVA}%</td>
                                <td>{formatPrice(lineTotal)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr>
                            <th colSpan="4" className="text-end">
                              {t("customerReturns.form.totals.totalHT")}:
                            </th>
                            <th>{formatPrice(currentReturn.montantHT)}</th>
                          </tr>
                          <tr>
                            <th colSpan="4" className="text-end">
                              {t("customerReturns.form.totals.totalVAT")}:
                            </th>
                            <th>{formatPrice(currentReturn.montantTVA)}</th>
                          </tr>
                          <tr>
                            <th colSpan="4" className="text-end">
                              {t("customerReturns.form.totals.totalTTC")}:
                            </th>
                            <th>{formatPrice(currentReturn.montantTTC)}</th>
                          </tr>
                        </tfoot>
                      </Table>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default CustomerReturnDetail;
